import { useSetRecoilState, useResetRecoilState } from "recoil"
import signupStepState from "../../../recoil/signupStep/atom"
import styled from "styled-components"
import { useNavigate } from "react-router-dom"
import { usePolicy } from "../../../hooks/usePolicy"
import { TextSpan } from "../../common/Text"
import { StyledButton } from "../../common/Buttons"
import { useFormContext } from "react-hook-form"
import { useUserRegister } from "../../../hooks/useAuth"

const SignupFormStep3 = () => {
  const setSignupStep = useSetRecoilState(signupStepState)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useFormContext()

  const { isLoading: isPrivacyLoading, data: privacyTermsHTML } =
    usePolicy("privacy")
  const { isLoading: isServiceLoading, data: serviceTermsHTML } =
    usePolicy("service")
  const { mutate: userRegister } = useUserRegister()

  const navigate = useNavigate()
  const resetSignupStep = useResetRecoilState(signupStepState)

  const signup = handleSubmit((data) => {
    userRegister({
      userRegisterPayload: {
        username: data.email,
        password: data.password,
        password2: data.passwordConfirm,
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
      },
      userInfoRegisterPayload: {
        username: data.email,
        position: data.position,
        mobile_carrier: data.phoneNumber.mobileCarrier,
        phone_number: data.phoneNumber.phoneNumber,
        advertisement: data.advertisement,
        school: data.schoolInfo.school,
        grade: data.schoolInfo.grade,
      },
    })
    navigate("/user/mypage")
    resetSignupStep()
  })

  return (
    <>
      <InputBox>
        <TermsBox>
          <TermsTitle>
            <TextSpan fontSize={17} fontWeight="bold">
              개인정보 이용 약관
            </TextSpan>
            <CheckBoxLabel>
              <CheckBoxInput
                {...register("agreedPrivacyTerms")}
                $isError={errors.agreedPrivacyTerms ? true : false}
              />
              <TextSpan fontSize={15}>동의합니다</TextSpan>
            </CheckBoxLabel>
          </TermsTitle>
          <TermsDescriptionBox>
            {isPrivacyLoading ? (
              <div>약관을 불러오는 중입니다.</div>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: privacyTermsHTML }} />
            )}
          </TermsDescriptionBox>
        </TermsBox>
        <TermsBox>
          <TermsTitle>
            <TextSpan fontSize={17} fontWeight="bold">
              서비스 이용 약관
            </TextSpan>
            <CheckBoxLabel>
              <CheckBoxInput
                {...register("agreedServiceTerms")}
                $isError={errors.agreedServiceTerms ? true : false}
              />
              <TextSpan fontSize={15}>동의합니다</TextSpan>
            </CheckBoxLabel>
          </TermsTitle>
          <TermsDescriptionBox>
            {isServiceLoading ? (
              <div>약관을 불러오는 중입니다.</div>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: serviceTermsHTML }} />
            )}
          </TermsDescriptionBox>
        </TermsBox>
      </InputBox>
      <NavigationButtonBox>
        <StyledButton $size="large" onClick={() => setSignupStep(2)}>
          이전
        </StyledButton>
        <StyledButton $size="large" onClick={signup}>
          회원 가입 완료
        </StyledButton>
      </NavigationButtonBox>
    </>
  )
}

export default SignupFormStep3

const InputBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`

const TermsBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const TermsTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`

const TermsDescriptionBox = styled.div`
  padding: 10px 5px 10px 10px;
  background-color: var(--custom-white-smoke);
  border-radius: 12px;
  border: 2px solid var(--custom-platinum);
  box-sizing: border-box;
  font-size: 15px;
  height: 150px;
  overflow: auto;

  &::-webkit-scrollbar {
    width: 20px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--custom-beige);
    width: 10px;
    border-radius: 10px;
    background-clip: padding-box;
    border: 5px solid transparent;
    min-height: 40px;
  }

  &::-webkit-scrollbar-button:vertical:end:decrement {
    display: block;
    height: 8px;
    background-color: transparent;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
    border-radius: 10px;
  }
`

const CheckBoxInput = styled.input.attrs(() => ({
  type: "checkbox",
}))<{ $isError: boolean }>`
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  width: 17px;
  height: 17px;
  border-radius: 4px;
  outline: 0;
  cursor: pointer;
  position: relative;

  ${(props) => props.$isError && "box-shadow: 0 0 5px var(--custom-light-red);"}

  &:before {
    content: "";
    position: absolute;
    top: 0px;
    left: 0px;
    border: 1px solid var(--custom-gray);
    border-radius: 4px;
    display: inline-block;
    height: 17px;
    width: 17px;
    box-sizing: border-box;
  }

  &:checked::before {
    content: "";
    background-image: url("/img/check.png");
    background-size: 100% 100%;
    background-repeat: no-repeat;
    background-position: center;
    width: 17px;
    height: 17px;
    position: absolute;
    top: 0px;
    left: 0px;
    display: inline-block;
    border: none;
  }
`

const CheckBoxLabel = styled.label`
  > span {
    cursor: pointer;
    padding-left: 2px;
  }

  > * {
    vertical-align: middle;
  }
`

const NavigationButtonBox = styled.div`
  display: flex;
  gap: 10px;
`
